import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, type FormikProps } from 'formik';
import shortid from 'shortid';
import { Text } from '@rsos/base-components';
import { Box, Button } from '@rsos/components/capstone/base';
import { useFlag } from '@rsos/flags';
import { selectExistingRequest } from '@rsos/reduxDataStore';
import {
  setFormPageToShow,
  resetPreselectedLandingZone,
} from '@rsos/reduxDataStore/src/features/airAmbulance/airAmbulanceSlice';
import { setSelectedTool } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneSlice';
import {
  AIR_AMBULANCE_CARD,
  closeCard,
} from '@rsos/reduxDataStore/src/features/mapCard/mapCardSlice';
import {
  closeResourcePanel,
  setResourcePanelView,
  useBreadCrumbs,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  trackOpenAirAmbulanceRequestForm,
  trackCloseAirAmbulanceRequestForm,
  trackLocationSelection,
  type LocationSelectionMethod,
} from '../../../gainsightTrackings/airAmbulanceTrackings';
import useAirAmbulanceEtlSubmit from '../etl/hooks/useAirAmbulanceEtlSubmit';
import useFormSubmit from '../midas/hooks/useFormSubmit';
import type {
  TentativeETA,
  ExistingRequest,
  Coordinates,
  SelectOption,
  SpatialObject,
} from '../types';
import { ButtonContainer, Loader } from './AirAmbulanceFormStandalone.styles';
import SelectLocationBanner from './SelectLocationBanner/SelectLocationBanner';
import ContactSection from './components/ContactSection';
import LocationSection from './components/LocationSection';
import RequestSection from './components/RequestSection';
import { FORM_PAGES, LAUNCH_TYPES } from './constants';
import { useInitialFormValues } from './hooks';
import { checkLaunchType } from './utils';
import { validateForm, parseCoordinates } from './validation';

interface AirAmbulanceState {
  airAmbulance: {
    preselectedLandingZone: SpatialObject | null;
  };
}

interface FormValues {
  pickup_coordinates: string;
  pickup_location: string;
  pickup_details: string;
  ground_contact: string;
  patient_info: string;
  request_reason: string | SelectOption;
  multiple_units_requested: string;
  launch_type: string;
  air_service_declined: string;
  air_service_responding: string;
  landing_zone_external_id: string;
  [key: string]: string | SelectOption;
}

interface AirAmbulanceFormStandaloneProps {
  hasLocationToRender?: boolean;
  tentativeETA?: TentativeETA | null;
  fromMapCard?: boolean;
}

const AirAmbulanceFormStandalone = ({
  hasLocationToRender = false,
  tentativeETA = null,
  fromMapCard = false,
}: AirAmbulanceFormStandaloneProps): JSX.Element => {
  const dispatch = useDispatch();
  const breadCrumbs = useBreadCrumbs();
  const formRef = useRef<FormikProps<FormValues>>(null);
  const existingRequest = useSelector(
    selectExistingRequest,
  ) as ExistingRequest | null;
  const preselectedLandingZone = useSelector(
    (state: AirAmbulanceState) => state.airAmbulance.preselectedLandingZone,
  );
  const isEtlIntegrationEnabled = useFlag('air_ambulance_etl_integrations');
  const hasGenericResourceRequests = useFlag('generic_resource_requests');

  const requestId = useMemo(() => {
    return existingRequest?.request_id;
  }, [existingRequest]);

  const extTripId = useMemo(() => {
    return existingRequest?.body?.ext_trip_id;
  }, [existingRequest]);

  const [isLoading, setIsLoading] = useState(false);
  const [isLocationValid, setIsLocationValid] = useState(
    hasLocationToRender || !!existingRequest?.body,
  );
  const initialValues = useInitialFormValues() as FormValues;
  const UUID = useMemo(() => shortid.generate(), []);

  const onLocationSelect = useCallback(
    (coords: Coordinates, method: LocationSelectionMethod) => {
      trackLocationSelection({
        requestId: extTripId || requestId,
        location: coords,
        selectionMethod: method,
        uuid: UUID,
      });
    },
    [extTripId, requestId, UUID],
  );

  // Track initial location on mount
  useEffect(() => {
    if (initialValues.pickup_coordinates) {
      const coords = parseCoordinates(initialValues.pickup_coordinates);
      if (coords) {
        const method = existingRequest?.body
          ? 'existing_request'
          : 'call_location';
        onLocationSelect(coords, method);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingRequest, initialValues, extTripId, requestId, UUID]);

  // Preselected landing zone: clear Redux state once LocationSection picks it up
  useEffect(() => {
    if (preselectedLandingZone) {
      dispatch(resetPreselectedLandingZone());
    }
  }, [preselectedLandingZone, dispatch]);

  // Standard Midas submission hook
  const handleMidasSubmit = useFormSubmit(
    isLoading,
    setIsLoading,
    tentativeETA,
    UUID,
    fromMapCard,
  );

  // ETL submission hook (always called but only used when flag is enabled)
  const handleEtlSubmit = useAirAmbulanceEtlSubmit({
    isLoading,
    setIsLoading,
    UUID,
  });

  const handleSubmit = isEtlIntegrationEnabled
    ? handleEtlSubmit
    : handleMidasSubmit;

  useEffect(() => {
    trackOpenAirAmbulanceRequestForm({ uuid: UUID });
  }, [UUID]);

  useEffect(() => {
    return () => {
      if (existingRequest) {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
      }
    };
  }, [dispatch, existingRequest]);

  const handleCancel = (): void => {
    if (hasGenericResourceRequests) {
      if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
        dispatch(setResourcePanelView('list_requests'));
      } else {
        dispatch(closeResourcePanel());
      }
    } else {
      if (!hasLocationToRender) {
        dispatch(closeCard(AIR_AMBULANCE_CARD));
      }
      if (existingRequest) {
        dispatch(setFormPageToShow(FORM_PAGES.STATUS));
      } else {
        dispatch(setSelectedTool(null));
      }
    }

    trackCloseAirAmbulanceRequestForm();
  };

  const hasErrors = useCallback(
    (values: FormValues): boolean => {
      if (
        values?.ground_contact?.length === 0 ||
        values?.patient_info?.length === 0 ||
        values?.request_reason === 'Select' ||
        values?.air_service_declined === '' ||
        values?.air_service_responding === '' ||
        !isLocationValid
      ) {
        return true;
      }
      return false;
    },
    [isLocationValid],
  );

  // Reverse geocode on mount when we have coordinates but may not have an address
  const needsReverseGeocode =
    !!initialValues.pickup_coordinates &&
    (!existingRequest?.body || !initialValues.pickup_location);

  return (
    <Box>
      <Box mb={3}>
        {tentativeETA?.providers && (
          <Text color="primaryText" size="small" italic>
            {tentativeETA.providers}
          </Text>
        )}
      </Box>
      <SelectLocationBanner />
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        validate={validateForm}
        validateOnMount
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {formikProps => {
          const {
            values,
            touched,
            handleBlur,
            handleChange,
            setFieldValue,
            setFieldTouched,
          } = formikProps;
          const secondaryLaunchTypeSelected = checkLaunchType(
            values.launch_type,
          );

          return (
            <Form id="airAmbulanceForm">
              <Box mx="5px">
                <LocationSection
                  values={values}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  formRef={formRef}
                  initialCoordinates={initialValues.pickup_coordinates}
                  initialAddressValid={
                    hasLocationToRender || !!existingRequest?.body
                  }
                  initialLandingZone={preselectedLandingZone}
                  reverseGeocodeOnMount={needsReverseGeocode}
                  onLocationSelect={onLocationSelect}
                  onAddressValidityChange={setIsLocationValid}
                  requestId={extTripId || requestId}
                />

                <ContactSection
                  values={values}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <RequestSection
                  values={values}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                />

                <Box pt={4}>
                  <ButtonContainer horizontal>
                    <Button
                      grow
                      btnType="tertiary"
                      type="button"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      grow
                      type="submit"
                      btnType="primary"
                      disabled={isLoading || hasErrors(values)}
                    >
                      {isLoading ? (
                        <Loader />
                      ) : secondaryLaunchTypeSelected === LAUNCH_TYPES.TEST ? (
                        'Submit Test'
                      ) : secondaryLaunchTypeSelected ===
                        LAUNCH_TYPES.STANDBY ? (
                        'Submit Standby'
                      ) : (
                        'Submit'
                      )}
                    </Button>
                  </ButtonContainer>
                </Box>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default AirAmbulanceFormStandalone;
